/**
 * Fetches facet (filter) counts for medical PA orders from the Postgres backend.
 * @module getFacetsCountFromPostgres
 */

import { PostgresFacetCountResponseModel } from "../../../data-model/postgresFacetCountResponseModel";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for retrieving aggregated facet counts from Postgres. */
export interface GetFacetsCountFromPostgresParams {
  org_id: string;
  assigned_to?: string;
  /** Facet attribute keys to aggregate (e.g. regimen_name, primary_payer_name). */
  facet_attributes?: string[];
  /** Active filter selections keyed by facet attribute. */
  facet_filters?: Record<string, string[]>;
  /** Client timezone offset in minutes; used for date-bucketing. */
  timezone_offset?: number;
  date_of_work_start?: string;
  date_of_work_end?: string;
  date_of_service_start?: string;
  date_of_service_end?: string;
  created_at_start?: string;
  created_at_end?: string;
}

/**
 * Fetches aggregated facet counts for medical PA orders from Postgres.
 *
 * @param params - Query parameters including org, assignee, and optional facet/filter criteria.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns Facet count results grouped by attribute.
 * @throws Re-throws any network or parsing error after logging.
 */
export const getFacetsCountFromPostgres = async (
  params: GetFacetsCountFromPostgresParams,
  signal?: AbortSignal,
): Promise<PostgresFacetCountResponseModel> => {
  const bearerToken = await getBearerToken();

  const raw = JSON.stringify({
    org_id: params.org_id,
    facet_attributes: params.facet_attributes,
    ...(params.assigned_to && { assigned_to: params.assigned_to }),
    facet_filters: params.facet_filters,
    timezone_offset: params.timezone_offset,
    date_of_work_start: params.date_of_work_start,
    date_of_work_end: params.date_of_work_end,
    date_of_service_start: params.date_of_service_start,
    date_of_service_end: params.date_of_service_end,
    ...(params.created_at_start && {
      created_at_start: params.created_at_start,
    }),
    ...(params.created_at_end && { created_at_end: params.created_at_end }),
  });

  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
    body: raw,
    ...(signal && { signal }),
  };

  try {
    const response = await fetch(
      API_ENDPOINTS.GET_FACETS_COUNT_FROM_POSTGRES,
      requestOptions,
    );
    if (!response.ok)
      throw new Error(`Facet counts request failed (${response.status})`);
    const result = await response.json();
    return result as PostgresFacetCountResponseModel;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
